import { ref } from 'vue'

// ---- Types ----

export interface ConsentCookieContent {
  consent?: boolean
  statistics?: boolean
  marketing?: boolean
}

export interface GdprUiControlOptions {
  privacyUrl?: string
  cookiesUrl?: string
  usesMarketing?: boolean
  usesStatistics?: boolean
  force?: boolean
  onAccept?: (consent: ConsentCookieContent) => void
}

// ---- Config ----

export const COOKIE_NAME = 'CookieConsent'
export const DEFAULT_PRIVACY_URL = 'https://digitaltwins.arcadis.com/digitaltwins/privacy/'
export const DEFAULT_COOKIES_URL = 'https://www.arcadis.com/cookies'

export const SETTING_TEXT = {
  NECESSARY: {
    title: 'Necessary',
    description:
      'Necessary cookies help make a website usable by enabling basic functions like page navigation and access to secure areas of the website. The website cannot function properly without these cookies.'
  },
  STATISTICS: {
    title: 'Statistics',
    description:
      'Statistic cookies help website owners to understand how visitors interact with websites by collecting and reporting information anonymously.'
  },
  MARKETING: {
    title: 'Marketing',
    description:
      'Marketing cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third party advertisers.'
  }
}

// ---- Cookie management ----

function getCookie(): ConsentCookieContent | undefined {
  const match = document.cookie
    .split(';')
    .map((c) => c?.trim())
    .find((c) => c?.startsWith(`${COOKIE_NAME}=`))
  if (!match) return undefined
  try {
    return JSON.parse(
      decodeURIComponent(match?.slice(COOKIE_NAME.length + 1))
    ) as ConsentCookieContent
  } catch {
    return undefined
  }
}

export function setCookie(value: ConsentCookieContent): void {
  const expires = new Date()
  expires.setFullYear(expires.getFullYear() + 1)
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(value))};expires=${expires.toUTCString()};path=/;SameSite=Strict`
}

export function hasConsent(): boolean {
  return getCookie()?.consent === true
}

export function getStatus(): ConsentCookieContent {
  const cookie = getCookie()
  return {
    consent: cookie?.consent ?? false,
    statistics: cookie?.statistics ?? false,
    marketing: cookie?.marketing ?? false
  }
}

export function resetCookie(): void {
  document.cookie = `${COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Strict`
}

// ---- Reactive state ----

export const gdprVisible = ref(false)
export const gdprOptions = ref<GdprUiControlOptions | null>(null)

// ---- Public API ----

const load = (options?: GdprUiControlOptions | null): void => {
  if (options?.force && hasConsent()) resetCookie()
  if (!hasConsent()) {
    gdprOptions.value = options ?? null
    gdprVisible.value = true
  }
}

const GdprUiControl = { load, getStatus }

export default GdprUiControl
